import {
  isPlatformAdminProfile,
} from "common/Utilities/Role.utils";
import {
  hasStoredSelectedCustomer,
  parseStoredSelectedCustomer,
} from "common/Utilities/CustomerSelection.utils";

const getHttpClients = () => {
  const { httpClient } = require("./http-client");
  const { httpClientPAdmin } = require("./http-client-pAdmin");
  return { httpClient, httpClientPAdmin };
};

const PROFILE_STORAGE_KEY = "profile";

const readCachedProfile = () => {
  try {
    const raw = localStorage.getItem(PROFILE_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const resolveCustomerContextForHeaders = (profile, getCustomerCode) => {
  const resolvedProfile = profile ?? readCachedProfile();
  if (!resolvedProfile) {
    return { customerCode: null, userId: null };
  }

  // Platform Admin without a selected customer stays platform-scoped (no customer headers).
  if (
    isPlatformAdminProfile(resolvedProfile) &&
    !hasStoredSelectedCustomer()
  ) {
    return { customerCode: null, userId: null };
  }

  const userId = resolvedProfile?.userid;
  const fromGetter =
    typeof getCustomerCode === "function" ? getCustomerCode() : undefined;
  const stored = parseStoredSelectedCustomer();
  const storedSelected = hasStoredSelectedCustomer();

  const resolvedCustomerCode =
    (fromGetter != null && String(fromGetter).trim() !== ""
      ? fromGetter
      : null) ??
    (storedSelected ? stored?.customerCode : null) ??
    resolvedProfile?.activeCustomerCode;

  return {
    customerCode: resolvedCustomerCode,
    userId,
  };
};

const HEADER_CUSTOMER_CODE = "customer-code";
const HEADER_USER_ID = "user-id";

const OMIT_EXACT_PATHS = new Set([
  "/platform-admin-service/v1/me",
  "/platform-admin-service/v1/me/customerAccess",
  "/platform-admin-service/v1/me/effectiveAccess",
  "/platform-admin-service/v1/me/activeCustomer",
  "/platform-admin-service/v1/customers/active",
  "/platform-admin-service/v1/customers",
  "/platform-admin-service/v1/users/details",
]);

const normalizePath = (url = "") => {
  const raw = String(url).split("?")[0];
  if (raw.startsWith("http")) {
    try {
      return new URL(raw).pathname;
    } catch {
      return raw;
    }
  }
  return raw.replace(/\/+$/, "") || raw;
};

const USERS_V1_PREFIX = "/platform-admin-service/v1/users/";

const isCustomerScopedUsersPath = (path) => {
  if (!path.startsWith(USERS_V1_PREFIX)) {
    return false;
  }

  const sub = path.slice(USERS_V1_PREFIX.length);
  const rootScopedSegments = [
    "access",
    "exists",
    "group/cognito",
    "cognito",
  ];

  if (rootScopedSegments.some((segment) => sub.startsWith(segment))) {
    return true;
  }

  if (/^\d+\/customerAccess\/[^/]+/.test(sub)) {
    return true;
  }

  return false;
};

export const shouldOmitCustomerHeadersForRequest = (url = "", method = "get") => {
  const path = normalizePath(url);
  const verb = String(method ?? "get").toLowerCase();

  if (OMIT_EXACT_PATHS.has(path)) {
    return true;
  }

  if (path.startsWith("/platform-admin-service/v1/users/identifier/exists")) {
    return true;
  }

  if (path.startsWith("/platform-admin-service/v1/userInvites")) {
    return true;
  }

  if (path.startsWith("/platform-admin-service/v1/inviteAcceptances")) {
    return true;
  }

  if (path === "/platform-admin-service/v1/users") {
    return true;
  }

  if (path.startsWith(USERS_V1_PREFIX)) {
    return !isCustomerScopedUsersPath(path);
  }

  if (
    verb === "get" &&
    (path === "/platform-admin-service/v1/customers/module-access" ||
      path.startsWith("/platform-admin-service/v1/customers/module-access/"))
  ) {
    return true;
  }

  if (path === "/things/type/details" || path.startsWith("/things/type/details")) {
    return true;
  }

  return false;
};

export const stripCustomerHeadersFromRequestConfig = (config) => {
  if (!config?.headers) {
    return;
  }
  delete config.headers[HEADER_CUSTOMER_CODE];
  delete config.headers[HEADER_USER_ID];
};

export const clearCustomerContextHeaders = () => {
  const { httpClient, httpClientPAdmin } = getHttpClients();
  delete httpClient.defaults.headers.common[HEADER_CUSTOMER_CODE];
  delete httpClient.defaults.headers.common[HEADER_USER_ID];
  delete httpClientPAdmin.defaults.headers.common[HEADER_CUSTOMER_CODE];
  delete httpClientPAdmin.defaults.headers.common[HEADER_USER_ID];
};

export const applyCustomerContextHeaders = (userId, customerCode) => {
  const { httpClient, httpClientPAdmin } = getHttpClients();
  const cc = customerCode != null ? String(customerCode).trim() : "";
  const uid = userId != null ? String(userId).trim() : "";

  if (uid) {
    httpClient.defaults.headers.common[HEADER_USER_ID] = uid;
    httpClientPAdmin.defaults.headers.common[HEADER_USER_ID] = uid;
  } else {
    delete httpClient.defaults.headers.common[HEADER_USER_ID];
    delete httpClientPAdmin.defaults.headers.common[HEADER_USER_ID];
  }

  if (cc) {
    httpClient.defaults.headers.common[HEADER_CUSTOMER_CODE] = cc;
    httpClientPAdmin.defaults.headers.common[HEADER_CUSTOMER_CODE] = cc;
  } else {
    delete httpClient.defaults.headers.common[HEADER_CUSTOMER_CODE];
    delete httpClientPAdmin.defaults.headers.common[HEADER_CUSTOMER_CODE];
  }
};

export const syncCustomerHeadersFromProfile = (profile, getCustomerCode) => {
  const { customerCode, userId } = resolveCustomerContextForHeaders(
    profile,
    getCustomerCode
  );

  if (customerCode && userId) {
    applyCustomerContextHeaders(userId, customerCode);
    return;
  }

  //from selected_customer.
  if (hasStoredSelectedCustomer()) {
    const stored = parseStoredSelectedCustomer();
    const uid = userId ?? profile?.userid;
    if (stored?.customerCode && uid) {
      applyCustomerContextHeaders(uid, stored.customerCode);
      return;
    }
  }

  clearCustomerContextHeaders();
};

const ensureCustomerHeadersOnRequest = (config, profile) => {
  const { httpClient } = getHttpClients();
  let { customerCode, userId } = resolveCustomerContextForHeaders(profile);

  if (hasStoredSelectedCustomer()) {
    if (!customerCode) {
      customerCode = httpClient.defaults.headers.common[HEADER_CUSTOMER_CODE];
    }
    if (!userId) {
      userId = httpClient.defaults.headers.common[HEADER_USER_ID];
    }
  }

  if (!customerCode || !userId) {
    stripCustomerHeadersFromRequestConfig(config);
    return;
  }

  config.headers = config.headers ?? {};
  config.headers[HEADER_CUSTOMER_CODE] = customerCode;
  config.headers[HEADER_USER_ID] = userId;
};

export const attachCustomerHeaderOmissionInterceptor = (client) => {
  client.interceptors.request.use((config) => {
    const url = config.url ?? "";
    if (shouldOmitCustomerHeadersForRequest(url, config.method)) {
      stripCustomerHeadersFromRequestConfig(config);
      return config;
    }

    ensureCustomerHeadersOnRequest(config, readCachedProfile());
    return config;
  });
};
